import {parse, stringify} from 'query-string';
import {combineLatest, BehaviorSubject, Observable, tap} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {LINKS} from '@px/const/links';
import {mc} from '@px/helper/mc';
import {contractUtil} from '@px/helper/contract';
import {numUtil} from '@px/helper/num';
import {mockUtil} from '@px/helper/moc';
import {contractStorage} from '@px/storage/contract';
import {spotStorage} from '@px/storage/spot';
import {marginStorage} from '@px/storage/margin';
import {spots$, contracts$, marginPairs$} from './config';
import {lang$} from './lang';

/**
 * https://phemex.com/trade/BTCUSDT
 * https://phemex.com/trade/inverse/BTCUSDT
 * https://phemex.com/trade/perpetual/BTCUSD
 * https://phemex.com/trade/all/uBTCUSD
 * https://phemex.com/meme/trade/41/2sCUCJdVkmyXp4dT8sFaA9LKgSMK4yDPi9zLHiwXpump
 */

const MOCK_CONTRACT_TRADE_PATHNAME = '/moc/trade';
const MOCK_INVERSE_CONTRACT_TRADE_PATHNAME = '/moc/trade/inverse';
const CONTRACT_TRADE_PATHNAME = LINKS.TRADE_FUTURES; //futures
const INVERSE_CONTRACT_TRADE_PATHNAME = LINKS.TRADE_INVERSE; //trade/inverse
const TRADE_PILOT_PATHNAME = LINKS.TRADE_PILOT; //trade/pilot'
const SPOT_TRADE_PATHNAME = LINKS.SPOT_TRADE; //trade
const MARGIN_TRADE_PATHNAME = LINKS.PHEMEX_MARGIN_TRADE; //margin/trade
const MEME_TRADE_PATHNAME = LINKS.PHEMEX_MEME_TRADE; //meme/trade

const RegexPatterns = {
  tradeAll: /\/trade\/all/,
  futures: /\/futures/,
  futuresInverse: /\/futures\/inverse/,
  futuresPilot: /\/futures\/pilot/,
  spot: /\/trade/,
  margin: /\/margin\/trade/,
  meme: /\/meme\/trade/,
  futuresMock: /\/moc\/trade/,
  futuresInverseMock: /\/moc\/trade\/inverse/,
};

const win = window;
const isMock = mockUtil.isMock();

export const url$: BehaviorSubject<Url> = new BehaviorSubject<Url>(null);
let preUrl: Url = null;

url$.pipe(filter(Boolean), tap(recordPreUrl)).subscribe((url) => {
  syncPathname(url);
  syncMockStatus(url);
});

interface SymbolChangeParams {
  type: TradeType;
  mode?: TradeMode;
  symbol: string;
  chainId?: number;
}

const symbolChange$ = new Observable<SymbolChangeParams>((observer) => {
  const unsubscribe = mc.subscribe('trade-pair', 'change', (payload: SymbolChangeParams) => observer.next(payload));
  return {unsubscribe};
});

combineLatest([symbolChange$, spots$, contracts$, marginPairs$, lang$])
  .pipe(map(convertUrl), filter(Boolean))
  .subscribe((url) => url$.next(url));

export function initUrl() {
  combineLatest([spots$, contracts$, marginPairs$, lang$])
    .pipe(map(normalizeUrl))
    .subscribe((url) => url$.next(url));
}

/**
 * pathname 不变仅修改 queryString
 * @param query
 */
export function updateQuery(query: Record<string, any>) {
  const url = url$.getValue();
  const q = stringify(query);
  const search = q ? `?${q}` : '';
  const href = url.origin + url.pathname + search;

  url$.next({
    ...url,
    href,
    query,
    search,
  });
}

function syncPathname(url: Url) {
  if (url.href === win.location.href) {
    return;
  }
  win.history.replaceState({}, null, url.href);
}

function normalizeUrl([spots, contracts, marginPairs, lang]: [SpotPair[], ContractPair[], MarginPair[], Language]): Url {
  const {origin, pathname, search} = win.location;
  const query = parse(search) as UrlQuery;
  const tradePair = normalizeTradePair(pathname, spots, contracts, marginPairs, lang);
  const newPathname = calcPathname(tradePair, lang);
  const href = calcHref(origin, newPathname, search);
  const tradeType = tradePair.type;
  return {origin, href, pathname: newPathname, search, query, tradeType, tradePair, isPerpetual: contractUtil.isPerpetual(tradeType)};
}

function convertUrl([params, spots, contracts, marginPairs, lang]: [SymbolChangeParams, SpotPair[], ContractPair[], MarginPair[], Language]): Url {
  const {origin, search, query} = url$.getValue();
  const tradePair = findTradePair(params, spots, contracts, marginPairs);
  if (!tradePair) {
    return null;
  }
  const newPathname = calcPathname(tradePair, lang);
  const href = calcHref(origin, newPathname, search);
  const tradeType = tradePair.type;
  return {origin, href, pathname: newPathname, search, query, tradeType, tradePair, isPerpetual: contractUtil.isPerpetual(tradeType)};
}

function findTradePair(params: SymbolChangeParams, spots: SpotPair[], contracts: ContractPair[], marginPairs: MarginPair[]): TradePair {
  if (params.mode === 'Margin') {
    if (!params.symbol) {
      const pair = normalizeMarginPair('', marginPairs);
      return marginPairs.find((o) => o.symbol === pair.symbol);
    }
    return marginPairs.find((o) => o.symbol === params.symbol);
  }

  if (params.type === 'Spot') {
    if (!params.symbol) {
      const pair = normalizeSpotPair('', spots);
      return spots.find((o) => o.symbol === pair.symbol);
    }
    return spots.find((o) => o.symbol === params.symbol);
  }
  if (contractUtil.isPerpetual(params.type)) {
    if (!params.symbol) {
      const pair = params.type === 'PerpetualPilot' ? normalizePilotContractPair('', contracts) : normalizeContractPair(true, '', contracts);
      return contracts.find((o) => o.symbol === pair.symbol);
    }
    return contracts.find((o) => o.symbol === params.symbol);
  }

  if (params.type === 'Meme') {
    return {type: params.type, pairName: params.symbol, chainId: params.chainId} as TradePair;
  }

  return null;
}

function normalizeTradePair(pathname: string, spots: SpotPair[], contracts: ContractPair[], marginPairs: MarginPair[], lang: Language): TradePair {
  const parts = pathname.split('/').filter(Boolean);
  const urlSymbol = parts.length > 0 ? parts[parts.length - 1].replace('-', '') : '';
  const langPrefix = lang === 'en' ? '' : `/${lang}`;
  if (RegexPatterns.tradeAll.test(pathname)) {
    return normalizeAllTradePair(urlSymbol, spots, contracts);
  }

  if (RegexPatterns.margin.test(pathname)) {
    return normalizeMarginPair(urlSymbol, marginPairs);
  }

  if (RegexPatterns.meme.test(pathname)) {
    const meme_parts = `${langPrefix}${MEME_TRADE_PATHNAME}`.split('/').filter(Boolean);
    const urlSymbol = meme_parts.length < parts.length ? parts[parts.length - 1] : '';
    const chainId = meme_parts.length < parts.length ? Number(parts[parts.length - 2]) : null;
    return {type: 'Meme', pairName: urlSymbol, chainId} as TradePair;
  }

  if (RegexPatterns.futuresPilot.test(pathname)) {
    return normalizePilotContractPair(urlSymbol, contracts) || createDefaultContractPair(contracts, false);
  }

  const isInverse = RegexPatterns.futuresInverse.test(pathname) || RegexPatterns.futuresInverseMock.test(pathname);
  const isContract = RegexPatterns.futures.test(pathname) || RegexPatterns.futuresMock.test(pathname);
  if (isInverse || isContract) {
    return normalizeContractPair(isInverse, urlSymbol, contracts);
  }

  if (RegexPatterns.spot.test(pathname)) {
    return normalizeSpotPair(urlSymbol, spots);
  }

  return createDefaultContractPair(contracts, false);
}

function normalizeAllTradePair(symbol: string, spots: SpotPair[], contracts: ContractPair[]): TradePair {
  const spot = spots.find((o) => o.symbol === symbol);
  if (spot) {
    return spot;
  }
  const contract = contracts.find((o) => o.symbol === symbol);
  if (contract) {
    return contract;
  }
  return createDefaultContractPair(contracts, false);
}

function normalizePilotContractPair(rowSymbol: string, contracts: ContractPair[]): ContractPair {
  const pilotContracts = contracts.filter((o) => contractUtil.isPilotContract(o.perpProductSubType));
  if (pilotContracts.length === 0) {
    return null;
  }
  const symbol = calcContractSymbol(rowSymbol.toUpperCase(), false);
  const contract = findMatchTradePair(pilotContracts, symbol);
  const contractSide = 1;
  if (contract && contract.contractSide === contractSide) {
    return contract;
  }
  const lastSymbol = contractStorage.readContractStorage().pilotLastSymbol;
  if (lastSymbol) {
    const lastContract = findMatchTradePair(contracts, lastSymbol);
    if (lastContract && lastContract.contractSide === contractSide) {
      return lastContract;
    }
  }
  return pilotContracts[0];
}

function normalizeSpotPair(rawSymbol: string, spots: SpotPair[]): SpotPair {
  const symbol = calcSpotSymbol(rawSymbol.toUpperCase());
  return findFirstMatchTradePair(spots, [symbol, spotStorage.readSpotStorage().lastSymbol, 'sBTCUSDT'].filter(Boolean));
}

function normalizeMarginPair(rawSymbol: string, marginPairs: MarginPair[]): MarginPair {
  const symbol = calcSpotSymbol(rawSymbol.toUpperCase());
  const defaultSymbol = marginPairs[0]?.symbol;
  return findFirstMatchTradePair(marginPairs, [symbol, marginStorage.readMarginStorage().lastSymbol, defaultSymbol].filter(Boolean));
}

function normalizeContractPair(isInverse: boolean, rawSymbol: string, contracts: ContractPair[]): ContractPair {
  const contractSide = isInverse ? -1 : 1;
  const symbol = calcContractSymbol(rawSymbol.toUpperCase(), isInverse);
  const contract = findMatchTradePair(contracts, symbol);
  if (contract && contract.contractSide === contractSide) {
    return contract;
  }
  const lastSymbol = contractStorage.readContractStorage().lastSymbol;
  if (lastSymbol) {
    const lastContract = findMatchTradePair(contracts, lastSymbol);
    if (lastContract && lastContract.contractSide === contractSide) {
      return lastContract;
    }
  }
  return createDefaultContractPair(contracts, isInverse);
}

function createDefaultContractPair(contracts: ContractPair[], isInverse: boolean): ContractPair {
  const defaultLinearSymbol = isMock ? 'BTCMUSDT' : 'BTCUSDT';
  const defaultInverseSymbol = isMock ? 'BTCUSD' : 'BTCUSD';
  return contracts.find((o) => o.symbol === (isInverse ? defaultInverseSymbol : defaultLinearSymbol));
}

function findMatchTradePair<T extends TradePair>(tradePairs: T[], symbol: string) {
  return tradePairs.filter(filterTradePair).find((o: T) => o.symbol === symbol);
}

function findFirstMatchTradePair<T extends TradePair>(tradePairs: T[], symbols: string[]) {
  for (let i = 0, len = symbols.length; i < len; i++) {
    const symbol = symbols[i];
    const tradePair = tradePairs.filter(filterTradePair).find((o: T) => o.symbol === symbol);
    if (tradePair) {
      return tradePair;
    }
  }
  return null;
}

function calcSpotSymbol(symbol: string) {
  // 现货加前缀 s
  return 's' + symbol;
}

function calcContractSymbol(symbol: string, isInverse: boolean) {
  if (symbol === 'BTCUSD') {
    return isInverse ? 'BTCUSD' : 'uBTCUSD';
  }
  //正向合约且首位为数字开头的symbol前缀加 u
  if (!isInverse && numUtil.startWithNumber(symbol)) {
    return 'u' + symbol;
  }
  // 反向合约加前缀 c
  return (isInverse ? 'c' : '') + symbol;
}

function calcPathnamePrefix(tradePair: TradePair): string {
  const {type, mode} = tradePair;
  if (type === 'Spot') {
    if (mode === 'Margin') {
      return MARGIN_TRADE_PATHNAME;
    }
    return SPOT_TRADE_PATHNAME;
  }
  if (contractUtil.isPerpetual(type)) {
    if (contractUtil.isPilotContract((tradePair as ContractPair).perpProductSubType)) {
      return TRADE_PILOT_PATHNAME;
    }
    return (tradePair as ContractPair).contractSide > 0
      ? isMock
        ? MOCK_CONTRACT_TRADE_PATHNAME
        : CONTRACT_TRADE_PATHNAME
      : isMock
      ? MOCK_INVERSE_CONTRACT_TRADE_PATHNAME
      : INVERSE_CONTRACT_TRADE_PATHNAME;
  }
  if (type === 'Meme') {
    return MEME_TRADE_PATHNAME;
  }
  return '';
}

function calcPathname(tradePair: TradePair, lang: Language): string {
  const pathnamePrefix = calcPathnamePrefix(tradePair);
  const langPrefix = lang === 'en' ? '' : `/${lang}`;
  const urlSymbol = genUrlSymbol(tradePair);
  return `${langPrefix}${pathnamePrefix}/${urlSymbol}`;
}

function calcHref(origin: string, pathname: string, search: string): string {
  return origin + pathname + search;
}

function filterTradePair(tradePair) {
  return ['PreList', 'Listed', 'Suspend', 'Delisted'].includes(tradePair.status);
}

function recordPreUrl(url: Url) {
  preUrl = JSON.parse(JSON.stringify(url));
}

/**
 * 运行中 mock 状态发生切换，重启整个应用
 */
function syncMockStatus(url: Url) {
  if (!preUrl) {
    return;
  }
  if (mockUtil.isMockUrl(preUrl.pathname) !== mockUtil.isMockUrl(url.pathname)) {
    win.location.reload();
  }
}

/**
 * Get the display URL symbol based on the given trade pair.
 *
 * @param {TradePair} tradePair The trade pair object containing type, pairName, urlSymbol.
 * @returns {string} The converted display URL symbol.
 */
function genUrlSymbol(tradePair: TradePair): string {
  const {type, pairName, chainId, urlSymbol} = tradePair;
  return type === 'Meme' ? (chainId && pairName ? `${chainId}/${pairName}` : '') : urlSymbol;
}
